import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectUserRole } from "../../../store/authSlice";
import {
  baGetLedger,
  baListUploads,
  baUploadStatement,
  baUpdateEntryLabel,
  baUpdateAccount,
  baExportLedger,
  baDeleteUpload,
} from "../../../services/bankAccounts";

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
// The ledger reads the way our own books (Tally) show a bank account, not the
// bank's passbook: money in = Debit, money out = Credit, and money in the
// bank is a Dr balance. Stored entries keep passbook sides (`credit` = money
// in, `debit` = money out; balance positive = money in), so the columns are
// mapped with bookDebit / bookCredit.
const fmtBalance = (n) => {
  const v = Number(n) || 0;
  return `${fmtMoney(Math.abs(v))} ${v < 0 ? "Cr" : "Dr"}`;
};
const bookDebit = (e) => e.credit || 0; // money in
const bookCredit = (e) => e.debit || 0; // money out
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—");
// Ledger dates are stored as UTC midnight — format in UTC so IST never
// shifts them a day.
const fmtUtcDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
const nextUtcDay = (d) => new Date(new Date(d).getTime() + 86400000);
const toInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");
// Receipt (money in) vs Payment (money out). Uses the
// statement's own voucher type when it was one of those two; otherwise — and
// for entries uploaded before voucher types were stored — derives it from
// which side carries the amount.
const entryType = (e) => {
  const own = String(e.vchType || "").trim();
  if (/^(receipt|payment|contra|journal)$/i.test(own)) return own[0].toUpperCase() + own.slice(1).toLowerCase();
  if ((e.credit || 0) > 0 && !(e.debit || 0)) return "Receipt";
  if ((e.debit || 0) > 0 && !(e.credit || 0)) return "Payment";
  return "";
};
const typeBadgeClass = (t) => {
  if (t === "Receipt") return "badge bg-success-subtle text-success";
  if (t === "Payment") return "badge bg-danger-subtle text-danger";
  return "badge bg-secondary-subtle text-secondary"; // Contra / Journal
};

// Indian financial year: 1 Apr -> 31 Mar.
const financialYearRange = (yearsAgo = 0) => {
  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const start = new Date(Date.UTC(fyStartYear - yearsAgo, 3, 1));
  const end = new Date(Date.UTC(fyStartYear - yearsAgo + 1, 2, 31));
  return { from: toInput(start), to: toInput(end) };
};
const calendarYearRange = () => {
  const y = new Date().getFullYear();
  return { from: toInput(new Date(Date.UTC(y, 0, 1))), to: toInput(new Date(Date.UTC(y, 11, 31))) };
};

const Ledger = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("chronological"); // "chronological" | "byLabel"
  const [expandedLabel, setExpandedLabel] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [uploads, setUploads] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showSheets, setShowSheets] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fixingBalance, setFixingBalance] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deletingUploadId, setDeletingUploadId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    baGetLedger(id, { from: from || undefined, to: to || undefined })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id, from, to]);

  const refreshUploads = useCallback(() => {
    baListUploads(id)
      .then((r) => setUploads(r.data?.uploads || []))
      .catch(() => setUploads([]));
  }, [id]);

  useEffect(load, [load]);
  useEffect(refreshUploads, [refreshUploads, uploadResult]);

  const entries = data?.entries || [];
  const byLabel = data?.byLabel || [];
  const account = data?.account;
  const range = data?.range || {};

  // A split voucher (Tally "as per details") appears under each of its heads,
  // showing only that head's share of the amount.
  const entriesByLabel = useMemo(() => {
    const map = new Map();
    const add = (key, row) => {
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    };
    entries.forEach((e) => {
      if (e.splits && e.splits.length) {
        e.splits.forEach((sp, i) =>
          add(sp.label || "UNLABELLED", {
            ...e,
            _rowKey: `${e._id}-${i}`,
            debit: e.debit ? sp.amount : 0,
            credit: e.credit ? sp.amount : 0,
            narration: `${sp.head} (part of ${e.refNo ? `#${e.refNo}` : "voucher"} ${fmtMoney(e.debit || e.credit)})`,
          }),
        );
      } else {
        add(e.label || "UNLABELLED", e);
      }
    });
    return map;
  }, [entries]);

  const applyPreset = (preset) => {
    let r;
    if (preset === "fy") r = financialYearRange(0);
    else if (preset === "lastFy") r = financialYearRange(1);
    else if (preset === "calendar") r = calendarYearRange();
    else r = { from: "", to: "" };
    setFrom(r.from);
    setTo(r.to);
  };

  const startEdit = (entry) => {
    setEditingId(entry._id);
    setEditValue(entry.label || "");
  };
  const saveEdit = async (entryId) => {
    if (!editValue.trim()) return;
    try {
      await baUpdateEntryLabel(entryId, editValue.trim());
      setEditingId(null);
      load();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not update label." });
    }
  };

  const submitUpload = async () => {
    if (!file) {
      Swal.fire({ icon: "warning", title: "Choose a statement file first." });
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await baUploadStatement(id, fd);
      setUploadResult({ ok: true, ...res.data });
      setFile(null);
      load();
    } catch (err) {
      setUploadResult({
        ok: false,
        message: err?.response?.data?.message || "Upload failed.",
        upload: err?.response?.data?.upload,
      });
    } finally {
      setUploading(false);
    }
  };

  // One-click fix for the common cause of an opening-balance mismatch: the
  // account was created with the wrong Dr/Cr side. Re-bases the account (and every stored ledger entry) on the
  // sheet's own opening balance instead of re-uploading everything.
  const applySheetOpeningBalance = async (sheetOpeningBalance) => {
    const result = await Swal.fire({
      icon: "question",
      title: "Use the sheet's opening balance?",
      html: `Set this account's opening balance to <b>${fmtBalance(sheetOpeningBalance)}</b>?<br/>Every ledger entry's running balance will shift to match — nothing is re-uploaded.`,
      showCancelButton: true,
      confirmButtonText: "Apply",
    });
    if (!result.isConfirmed) return;
    setFixingBalance(true);
    try {
      await baUpdateAccount(id, { openingBalance: sheetOpeningBalance });
      setUploadResult(null);
      load();
      Swal.fire({ icon: "success", title: "Opening balance updated.", timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not update opening balance",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setFixingBalance(false);
    }
  };

  // Deletes one uploaded sheet's worth of ledger entries — never the whole
  // ledger. The backend recomputes the account's running balance from
  // whatever remains, so a wrongly-uploaded or duplicate sheet can be pulled
  // out without disturbing any other sheet's entries.
  const handleDeleteUpload = async (upload) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete this sheet?",
      html: `
        <div class="fw-semibold">${upload.originalFileName || "Untitled sheet"}</div>
        <div class="small text-muted">${fmtDate(upload.dateFrom)} → ${fmtDate(upload.dateTo)} · ${upload.rowsParsed || 0} rows</div>
        <div class="mt-2">This permanently removes every ledger entry from this sheet and recalculates the account's balance. This cannot be undone.</div>
      `,
      showCancelButton: true,
      confirmButtonText: "Delete sheet",
      confirmButtonColor: "#dc3545",
    });
    if (!result.isConfirmed) return;

    setDeletingUploadId(upload._id);
    try {
      await baDeleteUpload(upload._id);
      setUploads((prev) => prev.filter((u) => u._id !== upload._id));
      load();
      Swal.fire({ icon: "success", title: "Sheet deleted.", timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not delete sheet",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setDeletingUploadId(null);
    }
  };

  const exportThisAccount = async () => {
    setExporting(true);
    try {
      await baExportLedger([id], { from: from || undefined, to: to || undefined });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Export failed" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu={account?.name || "Ledger"} motherMenu="Reports · Netplus Banks" />
      <div className="mb-2">
        <Link to="/bank-accounts" className="text-decoration-none small">
          ← All accounts
        </Link>
      </div>

      {account && (
        <div className={`alert ${account.lastUploadClosingDate ? "alert-info" : "alert-secondary"} py-2 mb-3`}>
          {account.lastUploadClosingDate ? (
            <>
              Statements uploaded up to <strong>{fmtUtcDate(account.lastUploadClosingDate)}</strong>. Upload the
              next sheet from <strong>{fmtUtcDate(nextUtcDay(account.lastUploadClosingDate))}</strong> onwards.
            </>
          ) : (
            <>No statement uploaded yet for this account.</>
          )}
        </div>
      )}

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-sm-3">
              <div className="text-muted small">Account</div>
              <div className="fs-5 fw-semibold">{account?.name || "—"}</div>
            </div>
            <div className="col-sm-3">
              <div className="text-muted small">Opening balance (range)</div>
              <div className="fs-5">{fmtBalance(range.openingBalance)}</div>
            </div>
            <div className="col-sm-3">
              <div className="text-muted small">Closing balance (range)</div>
              <div className={`fs-5 fw-semibold ${range.closingBalance < 0 ? "text-danger" : ""}`}>
                {fmtBalance(range.closingBalance)}
              </div>
            </div>
            <div className="col-sm-3">
              <div className="text-muted small">Debit / Credit (range)</div>
              <div className="fs-6">
                <span className="text-success">{fmtMoney(range.totalCredit)}</span>
                {" / "}
                <span className="text-danger">{fmtMoney(range.totalDebit)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header d-flex flex-wrap gap-2 align-items-center">
          <div className="btn-group btn-group-sm me-2" role="group">
            <button
              className={`btn ${view === "chronological" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setView("chronological")}
            >
              Ledger
            </button>
            <button
              className={`btn ${view === "byLabel" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setView("byLabel")}
            >
              By Category
            </button>
          </div>

          <div className="btn-group btn-group-sm me-2" role="group">
            <button className="btn btn-outline-secondary" onClick={() => applyPreset("fy")}>
              This FY
            </button>
            <button className="btn btn-outline-secondary" onClick={() => applyPreset("lastFy")}>
              Last FY
            </button>
            <button className="btn btn-outline-secondary" onClick={() => applyPreset("calendar")}>
              Calendar Year
            </button>
            <button className="btn btn-outline-secondary" onClick={() => applyPreset("all")}>
              All time
            </button>
          </div>

          <input
            type="date"
            className="form-control form-control-sm"
            style={{ width: 150 }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <span className="small text-muted">to</span>
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ width: 150 }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />

          <div className="ms-auto d-flex gap-2">
            <button className="btn btn-outline-primary btn-sm" disabled={exporting} onClick={exportThisAccount}>
              {exporting ? "Exporting…" : "Download Excel"}
            </button>
            <button
              className={`btn btn-sm ${showSheets ? "btn-secondary" : "btn-outline-secondary"}`}
              onClick={() => setShowSheets((v) => !v)}
            >
              {showSheets ? "Hide Sheets" : "Manage Sheets"}
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowUpload((v) => !v)}>
              Upload Statement
            </button>
          </div>
        </div>

        {showUpload && (
          <div className="card-body border-bottom bg-light">
            {account?.lastUploadClosingDate && (
              <div className="small text-muted mb-2">
                Already uploaded up to {fmtUtcDate(account.lastUploadClosingDate)} — this sheet should start from{" "}
                <strong>{fmtUtcDate(nextUtcDay(account.lastUploadClosingDate))}</strong>.
              </div>
            )}
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="form-control form-control-sm"
                style={{ maxWidth: 320 }}
                onChange={(e) => setFile(e.target.files[0] || null)}
              />
              <button className="btn btn-primary btn-sm" disabled={uploading} onClick={submitUpload}>
                {uploading ? "Uploading…" : "Submit"}
              </button>
            </div>

            {uploadResult && (
              <div className={`alert mt-3 mb-0 ${uploadResult.ok ? "alert-success" : "alert-danger"}`}>
                <div className="fw-semibold">{uploadResult.message}</div>
                {uploadResult.summary && (
                  <ul className="small mb-0 mt-1">
                    <li>Rows parsed: {uploadResult.summary.rowsParsed}, skipped: {uploadResult.summary.rowsSkipped}, duplicate: {uploadResult.summary.rowsDuplicate}, added: {uploadResult.summary.insertedCount}</li>
                    {uploadResult.summary.dateFrom && (
                      <li>
                        Sheet range: {fmtDate(uploadResult.summary.dateFrom)} to {fmtDate(uploadResult.summary.dateTo)}
                      </li>
                    )}
                    {uploadResult.summary.balanceContinuityOk === false && (
                      <li className="text-danger fw-semibold d-flex flex-wrap align-items-center gap-2">
                        <span>
                          Opening balance mismatch of {fmtMoney(Math.abs(uploadResult.summary.balanceMismatchAmount))} — see warnings below.
                        </span>
                        {uploadResult.summary.openingBalanceSheet != null && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            disabled={fixingBalance}
                            onClick={() =>
                              applySheetOpeningBalance(uploadResult.summary.openingBalanceSheet)
                            }
                          >
                            {fixingBalance
                              ? "Applying…"
                              : `Use sheet's opening balance (${fmtBalance(uploadResult.summary.openingBalanceSheet)})`}
                          </button>
                        )}
                      </li>
                    )}
                  </ul>
                )}
                {(uploadResult.summary?.warnings || uploadResult.upload?.validation?.warnings || []).length > 0 && (
                  <ul className="small mb-0 mt-2 text-warning-emphasis">
                    {(uploadResult.summary?.warnings || uploadResult.upload?.validation?.warnings || []).map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
                {(uploadResult.upload?.validation?.errors || []).length > 0 && (
                  <ul className="small mb-0 mt-2 text-danger">
                    {uploadResult.upload.validation.errors.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {showSheets && (
          <div className="card-body border-bottom bg-light">
            <div className="small text-muted mb-2">
              Every sheet uploaded for this account. Deleting a sheet removes only its own entries — every other
              sheet is untouched — and recalculates the account's balance from what remains.
            </div>
            {uploads.length === 0 ? (
              <div className="text-muted small">No sheets uploaded yet.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Range</th>
                      <th>Rows</th>
                      <th>Balance check</th>
                      {isSuperAdmin && <th className="text-end">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {uploads.map((u) => (
                      <tr key={u._id}>
                        <td className="small">{u.originalFileName || "Untitled sheet"}</td>
                        <td className="small text-nowrap">
                          {fmtDate(u.dateFrom)} → {fmtDate(u.dateTo)}
                        </td>
                        <td className="small">
                          {u.rowsParsed} added{u.rowsDuplicate ? ` (dup ${u.rowsDuplicate})` : ""}
                        </td>
                        <td className="small">
                          {u.balanceContinuityOk === false ? (
                            <span className="text-danger">balance mismatch</span>
                          ) : u.balanceContinuityOk === true ? (
                            <span className="text-success">balance ok</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        {isSuperAdmin && (
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled={deletingUploadId === u._id}
                              onClick={() => handleDeleteUpload(u)}
                            >
                              {deletingUploadId === u._id ? "Deleting…" : "Delete sheet"}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">Loading…</div>
          ) : view === "chronological" ? (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Narration</th>
                    <th>Label</th>
                    <th>Ref No.</th>
                    <th>Type</th>
                    <th className="text-end">Debit</th>
                    <th className="text-end">Credit</th>
                    <th className="text-end">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center text-muted py-3">
                        No entries in this range.
                      </td>
                    </tr>
                  ) : (
                    entries.map((e) => (
                      <tr key={e._id}>
                        <td className="text-nowrap">{fmtDate(e.date)}</td>
                        <td>
                          {e.narration}
                          {e.splits && e.splits.length > 0 && (
                            <div className="small text-muted mt-1">
                              {e.splits.map((sp, i) => (
                                <div key={i}>
                                  ↳ {sp.head} — {fmtMoney(sp.amount)}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          {editingId === e._id ? (
                            <div className="d-flex gap-1">
                              <input
                                className="form-control form-control-sm"
                                value={editValue}
                                onChange={(ev) => setEditValue(ev.target.value)}
                                onKeyDown={(ev) => ev.key === "Enter" && saveEdit(e._id)}
                                autoFocus
                              />
                              <button className="btn btn-xs btn-success" onClick={() => saveEdit(e._id)}>
                                ✓
                              </button>
                            </div>
                          ) : (
                            <span
                              className="badge bg-light text-dark border"
                              style={{ cursor: "pointer" }}
                              onClick={() => startEdit(e)}
                              title="Click to edit"
                            >
                              {e.label || "UNLABELLED"}
                            </span>
                          )}
                        </td>
                        <td className="small text-muted">{e.refNo}</td>
                        <td>
                          {entryType(e) && (
                            <span className={typeBadgeClass(entryType(e))}>{entryType(e)}</span>
                          )}
                        </td>
                        <td className="text-end text-success">{bookDebit(e) ? fmtMoney(bookDebit(e)) : ""}</td>
                        <td className="text-end text-danger">{bookCredit(e) ? fmtMoney(bookCredit(e)) : ""}</td>
                        <td className="text-end fw-semibold">{fmtBalance(e.balance)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th aria-label="Expand" style={{ width: 24 }} />
                    <th>Label</th>
                    <th className="text-end">Entries</th>
                    <th className="text-end">Total Debit</th>
                    <th className="text-end">Total Credit</th>
                    <th className="text-end">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {byLabel.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-3">
                        No entries in this range.
                      </td>
                    </tr>
                  ) : (
                    byLabel.map((g) => (
                      <React.Fragment key={g.label}>
                        <tr
                          style={{ cursor: "pointer" }}
                          onClick={() => setExpandedLabel((cur) => (cur === g.label ? null : g.label))}
                        >
                          <td>{expandedLabel === g.label ? "▾" : "▸"}</td>
                          <td className="fw-semibold">{g.label}</td>
                          <td className="text-end">{g.count}</td>
                          <td className="text-end text-success">{fmtMoney(g.credit)}</td>
                          <td className="text-end text-danger">{fmtMoney(g.debit)}</td>
                          <td className={`text-end fw-semibold ${g.net < 0 ? "text-danger" : ""}`}>
                            {fmtBalance(g.net)}
                          </td>
                        </tr>
                        {expandedLabel === g.label &&
                          (entriesByLabel.get(g.label) || []).map((e) => (
                            <tr key={e._rowKey || e._id} className="bg-light">
                              <td />
                              <td colSpan={2} className="small">
                                {fmtDate(e.date)} — {e.narration}{" "}
                                {entryType(e) && (
                                  <span className={typeBadgeClass(entryType(e))}>{entryType(e)}</span>
                                )}
                              </td>
                              <td className="text-end small text-success">{bookDebit(e) ? fmtMoney(bookDebit(e)) : ""}</td>
                              <td className="text-end small text-danger">{bookCredit(e) ? fmtMoney(bookCredit(e)) : ""}</td>
                              <td className="text-end small">{fmtBalance(e.balance)}</td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Ledger;
